import { useNavigate } from "react-router-dom";
import { FC, useCallback, useRef, useState } from "react";
import cn from "classnames";
import {
  Button,
  Container,
  Nullable,
  FileItem,
  FileUpload,
  useWillUnmount,
  useDidUpdate,
  useFileQueue,
  DropAcceptedFunc,
  notification,
  Checkbox,
} from "@epcnetwork/core-ui-kit";

import { getAcceptedFilesText, getPipelineRoute, getSupportedFormats, onFileRedirect } from "utils";
import { useTracking } from "hooks";
import { SMARTLOOK_EVENTS } from "constants/smartlook.constants";
import { PIPELINES_LIST_PAGE } from "constants/routes.constants";
import { suppressionCheckboxLabel } from "constants/pipelines.constants";
import { postDomainPurifyFile } from "api";
import { PipelineHeaderSection } from "../components/pipeline-header-section/pipeline-header-section";
import { DomainPurifyFormModal } from "./form-modal/domain-purify-form-modal";
import {
  DomainPurifyFileInfo,
  DomainPurifyFileItem,
  PipelineDomainPurifyData,
} from "./domain-purify.types";
import { supportedFormats, getInitialData } from "./domain-purify.constants";

import styles from "./domain-purify.module.css";

const DomainPurifyPage: FC = () => {
  const componentIsMounted = useRef(true);
  const navigate = useNavigate();

  const { pipelineSubmitEvent } = useTracking();

  const pipeline = getPipelineRoute("Domain Purify");

  const {
    files,
    createInitialFile,
    addFiles,
    updateFiles,
    removeFiles,
    clearEntity,
    getItem,
    isEntityInConfiguration,
    isEntityFinished,
    hasAllConfigured,
    submitOneByOne,
    hasAllSuccessFiles,
  } = useFileQueue<DomainPurifyFileItem, PipelineDomainPurifyData>(
    pipeline.title,
    postDomainPurifyFile,
  );

  useDidUpdate(() => {
    if (!isEntityFinished) return;

    handleRedirect();
  }, [isEntityFinished]);

  useWillUnmount(() => {
    componentIsMounted.current = false;
  });

  const [suppress, setSuppress] = useState(true);
  const [editedFile, setEditedFile] = useState<Nullable<DomainPurifyFileItem>>(null);

  const handleAcceptedDrop: DropAcceptedFunc = useCallback(
    (acceptedFiles) => {
      addFiles(
        acceptedFiles.map((file) => createInitialFile(file, { data: getInitialData(file) })),
      );
    },
    [addFiles, createInitialFile],
  );

  const handleClearClick = () => {
    notification.confirm(
      "Delete files",
      `Are you sure you want to delete all ${files.length} file(s)?`,
      {
        onOk: clearEntity,
        icon: "delete",
      },
    );
  };

  const handleConfigureItem = useCallback(
    (id: string) => {
      const item = getItem(id);
      if (!item) return;
      setEditedFile(item);
    },
    [getItem],
  );

  const handleModalSubmit = useCallback(
    ({ id, ...rest }: DomainPurifyFileItem) => {
      updateFiles({ id, file: rest });
      setEditedFile(null);
    },
    [updateFiles],
  );

  const handleCloseModal = useCallback(() => setEditedFile(null), []);

  const handleSuppressChange = useCallback((value: string, isChecked: boolean) => {
    setSuppress(isChecked);
  }, []);

  const handleSubmit = () => {
    const data = files.map((file) => {
      const { name: filename } = file.originalFile;
      const { domains, match, useFile, domainFormFile } = file.data;

      const formData = new FormData();

      if (useFile && domainFormFile) {
        // use file as domain values
        const [domainFile] = domainFormFile;

        const fileInfo: DomainPurifyFileInfo = {
          suppress,
          match,
        };

        formData.append("fileInfo", JSON.stringify(fileInfo));
        formData.append("domainFile", domainFile.originalFile, domainFile.originalFile.name);
      } else {
        // use domains as string
        const fileInfo: DomainPurifyFileInfo = {
          suppress,
          match,
          domains,
        };

        formData.append("fileInfo", JSON.stringify(fileInfo));
      }

      formData.append("file", file.originalFile, filename);

      return {
        id: file.id,
        data: formData,
      };
    });

    submitOneByOne(data);
    pipelineSubmitEvent(SMARTLOOK_EVENTS.domainPurifySubmit, files);
  };

  const handleRedirect = () => {
    onFileRedirect({ files, clearEntity, navigate });
  };

  const handleRedirectToPipelines = () => {
    navigate(PIPELINES_LIST_PAGE.path);
  };

  return (
    <Container>
      <div className={styles.container}>
        <PipelineHeaderSection pipeline={pipeline} />

        <div className={styles.form}>
          <FileUpload
            className={cn(styles.dropZone, { [styles.uploadZone]: files.length })}
            uploadedFilesLength={files.length}
            subtitle={getAcceptedFilesText(getSupportedFormats(supportedFormats))}
            accept={supportedFormats}
            onDropAccepted={handleAcceptedDrop}
            exceedFilesOption="splice-with-error"
            disabled={!isEntityInConfiguration}
            preventDropOnDocument
            multiple
          />
          <div className={styles.fileList}>
            {files.map(({ id, originalFile, data, ...rest }) => (
              <FileItem
                {...rest}
                key={id}
                id={id}
                file={originalFile}
                onCrossClick={removeFiles}
                onSetValuesClick={handleConfigureItem}
                onEditValuesClick={handleConfigureItem}
              />
            ))}
          </div>
          {editedFile && (
            <DomainPurifyFormModal
              file={editedFile}
              setClose={handleCloseModal}
              onSubmitClick={handleModalSubmit}
            />
          )}
          <div className={styles.actions}>
            <Checkbox
              checked={suppress}
              value="suppress"
              onChange={handleSuppressChange}
              label={suppressionCheckboxLabel}
              disabled={false}
              disableError
            />
            <div className={styles.buttons}>
              <Button
                appearance="secondary"
                onClick={!files.length ? handleRedirectToPipelines : handleClearClick}
                disabled={isEntityFinished && !hasAllSuccessFiles}
              >
                Cancel
              </Button>
              {!isEntityFinished && (
                <Button
                  onClick={handleSubmit}
                  disabled={!hasAllConfigured || !files.length}
                  loading={!isEntityInConfiguration}
                >
                  Submit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export { DomainPurifyPage };
